import { useCallback, useEffect, useRef, useState } from "react";
import classes from "./StickyScrollX.module.scss";

/**
 * Horizontal scroll container whose scrollbar is always reachable at the
 * bottom of the viewport — the user shouldn't have to scroll the page
 * down to a tall canvas just to drag its horizontal scrollbar.
 *
 * The real content scroller hides its native horizontal scrollbar; a
 * proxy bar rendered after it is `position: sticky; bottom: 0`, so it
 * pins to the viewport bottom while the canvas is in view. The proxy's
 * inner spacer matches the content's scrollWidth, and the two scroll
 * positions are kept in sync both ways.
 *
 * Only renders the proxy bar when the content actually overflows
 * horizontally.
 */
export default function StickyScrollX({ children }) {
  const contentRef = useRef(null);
  const proxyRef = useRef(null);
  const syncingRef = useRef(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setScrollWidth(el.scrollWidth);
    setClientWidth(el.clientWidth);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const onContentScroll = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (proxyRef.current && contentRef.current) {
      proxyRef.current.scrollLeft = contentRef.current.scrollLeft;
    }
    syncingRef.current = false;
  };

  const onProxyScroll = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (proxyRef.current && contentRef.current) {
      contentRef.current.scrollLeft = proxyRef.current.scrollLeft;
    }
    syncingRef.current = false;
  };

  const overflowing = scrollWidth > clientWidth + 1;

  return (
    <>
      <div ref={contentRef} className={classes.content} onScroll={onContentScroll}>
        {children}
      </div>
      {overflowing && (
        <div
          ref={proxyRef}
          className={classes.stickyBar}
          onScroll={onProxyScroll}
          aria-hidden="true"
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}
    </>
  );
}
